import React from "react";
import axios from "axios";
import { useMediaQuery } from "react-responsive";
import { GlobalContext } from "../../../context/GlobalState";
import { ReactComponent as SideBoardButton } from "../../../images/analytics/SideboardControl.svg";

export default function SideBoard({
  filterData = {},
  setFilterData = () => {},
  setData = () => {},
  headerData = {},
  setSubjectData = () => {},
  disabled = false,
  levels = ["subject", "chapter", "topic"]
}) {
  const { userDetails } = React.useContext(GlobalContext);
  const isMobile = useMediaQuery({ query: "(max-width: 600px)" });

  const [open, setOpen] = React.useState(false);
  const [subjects, setSubjects] = React.useState([]);
  const [chapters, setChapters] = React.useState([]);
  const [topics, setTopics] = React.useState([]);

  const language = window.localStorage.getItem("language") || "";

  const showSubject = levels.includes("subject");
  const showChapter = levels.includes("chapter");
  const showTopic = levels.includes("topic");

  // ---------------- FETCH ----------------

  const fetchSubjects = async () => {
    try {
      if (!userDetails?.grade || !userDetails?.section) return;

      const res = await axios.get(
        `${process.env.REACT_APP_ORIGIN2}/api/v1/grades/${userDetails.grade}/sections/${userDetails.section}/subjects`,
        {
          headers: {
            token: window.localStorage.getItem("authToken") || "",
          },
        }
      );

      const data = res?.data?.data?.subjects || [];

      const filtered = data.filter(
        (item) =>
          item?.subjectData?.lang === language &&
          !item?.subjectData?.disabled
      );

      const mapped = filtered.map((item) => ({
        _id: item?.subjectId,
        name: item?.subject,
      }));

      setSubjects(mapped);
      setSubjectData(mapped);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchChapters = async () => {
    if (!showChapter || !filterData?.subjectId) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_ORIGIN2}/api/v1/subjects/${filterData.subjectId}`,
        {
          headers: {
            token: window.localStorage.getItem("authToken") || "",
          },
        }
      );

      const data = res?.data?.data?.subject?.chapters || [];

      setChapters(
        data.filter((c) => c?.lang === language && !c?.disabled)
      );
    } catch {
      setChapters([]);
    }
  };

  const fetchTopics = async () => {
    if (!showTopic || !filterData?.chapterId) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_ORIGIN2}/api/v1/chapters/${filterData.chapterId}`,
        {
          headers: {
            token: window.localStorage.getItem("authToken") || "",
          },
        }
      );

      const data = res?.data?.data?.chapter?.topics || [];

      setTopics(
        data.filter((t) => t?.lang === language && !t?.disabled)
      );
    } catch {
      setTopics([]);
    }
  };

  React.useEffect(() => {
    fetchSubjects();
  }, []);

  React.useEffect(() => {
    fetchChapters();
    if (!filterData?.subjectId) setChapters([]);
  }, [filterData?.subjectId, showChapter]);

  React.useEffect(() => {
    fetchTopics();
    if (!filterData?.chapterId) setTopics([]);
  }, [filterData?.chapterId, showTopic]);

  // ---------------- HANDLERS ----------------

  const handleSubject = (id, name) => {
    setFilterData((prev) => ({
      ...prev,
      subjectId: id === prev.subjectId ? "" : id,
      chapterId: "",
      topicId: "",
    }));

    setData((prev) => ({
      ...prev,
      subject: prev.subject === name ? "" : name,
      chapter: "",
      topic: "",
    }));

    if (isMobile) setOpen(false);
  };

  const handleChapter = (id, name) => {
    setFilterData((prev) => ({
      ...prev,
      chapterId: id === prev.chapterId ? "" : id,
      topicId: "",
    }));

    setData((prev) => ({
      ...prev,
      chapter: prev.chapter === name ? "" : name,
      topic: "",
    }));

    if (isMobile) setOpen(false);
  };

  const handleTopic = (id, topic) => {
    setFilterData((prev) => ({
      ...prev,
      topicId: id === prev.topicId ? "" : id,
    }));

    setData((prev) => ({
      ...prev,
      topic: prev.topic === topic?.name ? "" : topic?.name,
    }));

    if (isMobile) setOpen(false);
  };

  const isSelected = (id, key) => filterData?.[key] === id;

  // ---------------- ITEM ----------------

  const Item = ({ label, level = 0, selected, onClick }) => (
    <div
      onClick={() => !disabled && onClick()}
      style={{
        padding: "8px 12px",
        paddingLeft: 12 + level * 18,
        borderRadius: 6,
        fontSize: 13,
        fontWeight: selected ? 600 : 500,
        background: selected ? "#e0f2fe" : "transparent",
        color: selected ? "#0369a1" : "#374151",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {label}
    </div>
  );

  // ---------------- RENDER ----------------

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          position: "fixed",
          top: "120px",
          left: open ? 260 : 4,
          width: 40,
          height: 48,
          background: "#3b82f6",
          border: "none",
          borderRadius: "0 50% 50% 0",
          zIndex: 20,
          transition: "left 0.3s ease",
        }}
      >
        <SideBoardButton
          style={{
            transform: open ? "rotate(0deg)" : "rotate(180deg)",
            transition: "0.3s",
          }}
        />
      </button>

      {/* Sidebar */}
      <div
        style={{
          position: "fixed",
          top: "100px",
          left: 0,
          width: 260,
          height: "calc(100% - 100px)",
          background: "#fff",
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "0.3s",
          zIndex: 10,
          overflowY: "auto",
        }}
      >
        <div style={{ padding: 8 }}>
          {/* SUBJECTS */}
          {showSubject &&
            subjects.map((subject) => (
              <div key={subject._id}>
                <Item
                  label={subject.name}
                  selected={isSelected(subject._id, "subjectId")}
                  onClick={() =>
                    handleSubject(subject._id, subject.name)
                  }
                />

                {/* CHAPTERS */}
                {showChapter &&
                  filterData?.subjectId === subject._id &&
                  chapters.map((chapter) => (
                    <div key={chapter._id}>
                      <Item
                        label={chapter.name}
                        level={1}
                        selected={isSelected(chapter._id, "chapterId")}
                        onClick={() =>
                          handleChapter(chapter._id, chapter.name)
                        }
                      />

                      {/* TOPICS */}
                      {showTopic &&
                        filterData?.chapterId === chapter._id &&
                        topics.map((topic) => (
                          <Item
                            key={topic._id}
                            label={topic.name}
                            level={2}
                            selected={isSelected(topic._id, "topicId")}
                            onClick={() =>
                              handleTopic(topic._id, topic)
                            }
                          />
                        ))}
                    </div>
                  ))}
              </div>
            ))}
        </div>
      </div>
    </>
  );
}